import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { DviLearningDashboardSummary } from '@/hooks/useDviLearningDashboard';
import { pct } from './stageColors';

interface Props {
  data: DviLearningDashboardSummary | null | undefined;
  isLoading: boolean;
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

const UNAVAILABLE = 'Unavailable';
const count = (n: number | null) => (n == null ? UNAVAILABLE : n.toLocaleString());
const rate = (n: number | null) => (n == null ? UNAVAILABLE : pct(n));
const NETWORK_HINT = 'Not available per shop';

export function GlobalLearningSummary({ data, isLoading }: Props) {
  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }
  const counted = data.accepted_count != null && data.edited_count != null && data.rejected_count != null;
  const total = counted ? data.accepted_count! + data.edited_count! + data.rejected_count! : 0;
  const acceptRate = counted && total ? data.accepted_count! / total : null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Kpi label="Confirmed Findings" value={data.total_confirmed_findings.toLocaleString()} />
      <Kpi label="Analyzed Media" value={data.total_analyzed_media.toLocaleString()} />
      <Kpi label="Comparisons" value={data.total_comparisons.toLocaleString()} />
      <Kpi label="Components ≥ 10 samples" value={count(data.components_with_min_samples)} hint={data.components_with_min_samples == null ? NETWORK_HINT : undefined} />

      <Kpi label="In Suggestion" value={count(data.components_in_suggestion)} />
      <Kpi label="In Prefill" value={count(data.components_in_prefill)} />
      <Kpi label="In Job Suggestion" value={count(data.components_in_job_suggestion)} />
      <Kpi label="Automation Ready" value={count(data.components_automation_ready)} />

      <Kpi label="Avg Component Acc." value={rate(data.avg_component_accuracy)} />
      <Kpi label="Avg Severity Acc." value={rate(data.avg_severity_accuracy)} />
      <Kpi label="Avg Recommendation Acc." value={rate(data.avg_recommendation_accuracy)} />
      <Kpi label="Acceptance Rate" value={rate(acceptRate)} hint={counted ? `${data.accepted_count} accepted of ${total}` : NETWORK_HINT} />
    </div>
  );
}
